import React, { useEffect, useState } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { useTranslate, useNotify, required, FormDataConsumer, BooleanInput, TextInput, SelectInput } from 'react-admin'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { Delete, Add, Code, KeyboardArrowUp, KeyboardArrowDown, Visibility } from '@mui/icons-material'
import Button from '@mui/material/Button'
import { Grid, Hidden, Skeleton, Typography } from '@mui/material'
import { removeBannerBySectorByIds, removeSectorByIds } from 'src/components/pages/administration/helper'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'
import { bannerServices } from 'src/services/reservation/banner/Banner.service'
import { NumberInput } from 'src/components/uiElements/fields/NumberInput'
import { HTMLInputLanding } from './HTMLInputLanding'
import { PreviewLanding } from './PreviewLanding'

const BANNER_TYPE = 1
const HTML_TYPE = 2

export const ArrayInputSector = ({ editMode = true }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { control } = useFormContext()
  const [openModal, setOpenModal] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [loadingBanners, setLoadingBanners] = useState(false)
  const [banners, setBanners] = useState([])
  const [currentIndex, setCurrentIndex] = useState(null)
  const [currentBanner, setCurrentBanner] = useState(null)
  const { fields, append, remove, update, replace } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'sectors', // unique name for your Field Array
    keyName: '_id'
  })

  useEffect(() => {
    ;(async () => {
      await getAllBanners()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (confirmDelete) {
      if (currentBanner !== null && currentBanner !== undefined) {
        const banner = fields[currentIndex].banners[currentBanner]
        removeBannerBySectorByIds(banner, () => removeBannerBySector(currentIndex, currentBanner), editMode, notify)
      } else {
        removeSectorByIds(currentIndex, fields, remove, editMode, notify)
      }
      setOpenModal(false)
      setCurrentIndex(null)
      setCurrentBanner(null)
      setConfirmDelete(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete])

  const getAllBanners = async () => {
    setLoadingBanners(true)
    try {
      const allBanners = await bannerServices.getByAll({ limit: 999 })
      setLoadingBanners(false)
      if (allBanners) {
        setBanners(allBanners)
        return
      }
      setBanners([])
    } catch (error) {
      setBanners([])
      setLoadingBanners(false)
    }
  }

  const deleteSector = (originalIndex, sector, remove, editMode) => {
    if (editMode && !('new_register' in sector)) {
      setOpenModal(true)
      setCurrentIndex(originalIndex)
      setCurrentBanner(null)
      return
    }
    remove(originalIndex)
  }

  const removeBannerBySector = (indexSector, indexBanner) => {
    const sector = JSON.parse(JSON.stringify(fields[indexSector]))
    sector.banners = sector.banners.slice(0, indexBanner).concat(sector.banners.slice(indexBanner + 1))
    update(indexSector, sector)
  }

  const deleteBannerBySector = (indexSector, indexBanner, editMode) => {
    const banner = fields[indexSector].banners[indexBanner]
    if (editMode && !('new_register' in banner)) {
      setOpenModal(true)
      setCurrentIndex(indexSector)
      setCurrentBanner(indexBanner)
      return
    }

    removeBannerBySector(indexSector, indexBanner)
  }

  const addBannerBySector = (sectors, index) => {
    const sector = JSON.parse(JSON.stringify(sectors[index]))
    sector.banners.push({ banner_id: '', is_active: true, new_register: true })
    update(index, sector)
  }

  const handleChangeBannerBySector = (sector, indexSector, indexBanner, value) => {
    const banner = banners.find(elem => elem.id === value)
    const bannersSector = [...sector.banners]
    bannersSector[indexBanner] = {
      ...bannersSector[indexBanner],
      banner_relation: banner
    }
    const newSector = {
      ...sector,
      banners: bannersSector
    }
    update(indexSector, newSector)
  }

  const handleChangeOrder = (formData, index, order) => {
    const sectorAux = {...formData.sectors[index]}
    const indexAux = fields.findIndex(elem => elem.order === order)
    let newSectors = [...formData.sectors]
    newSectors[index] = { ...newSectors[indexAux], order: newSectors[index].order }
    newSectors[indexAux] = { ...sectorAux, order: newSectors[indexAux].order }

    replace(newSectors)
  }

  return (
    <>
      <ConfirmationDialog
        title={
          currentBanner !== null
            ? translate('ra.message.delete_banner')
            : translate('ra.message.delete_sector')
        }
        buttonConfirmText={translate('ra.action.to_confirm')}
        buttonCancel={translate('ra.action.cancel')}
        setOpen={setOpenModal}
        setConfirm={setConfirmDelete}
        open={openModal}
      />
      {React.Children.toArray(
        fields.map((field, index) => (
          <div
            key={field._id}
            style={{
              width: '100%',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '12px 0 0 6px',
            }}
          >
            <Grid container spacing={3}>
              <EditInputsWrap label={translate('ra.field.sector_type')} required>
                <SelectInput
                  source={`sectors.${index}.type`}
                  optionText='name'
                  optionValue='id'
                  fullWidth
                  choices={[
                    { id: BANNER_TYPE, name: 'Banners' },
                    { id: HTML_TYPE, name: 'HTML' },
                  ]}
                  label={false}
                  disabled={editMode && !('new_register' in field)}
                  validate={[required()]}
                />
              </EditInputsWrap>
              <FormDataConsumer>
                {({ formData }) => (
                  <EditInputsWrap label={translate('ra.field.order')} required>
                    <div style={{ display: 'flex' }}>
                      <NumberInput
                        source={`sectors.${index}.order`}
                        label={false}
                        noDecimal
                        validate={[required()]}
                      />
                      <div className='sort-field-container'>
                        <Button
                          variant='contained'
                          disabled={field.order === 1}
                          onClick={() => handleChangeOrder(formData, index, field.order - 1)}
                        >
                          <KeyboardArrowUp />
                        </Button>
                        <Button
                          variant='contained'
                          disabled={field.order === fields.length}
                          onClick={() => handleChangeOrder(formData, index, field.order + 1)}
                        >
                          <KeyboardArrowDown />
                        </Button>
                      </div>
                    </div>
                  </EditInputsWrap>
                )}
              </FormDataConsumer>
              <EditInputsWrap label={translate('ra.field.is_active')} md={1}>
                <BooleanInput
                  defaultValue={true}
                  source={`sectors.${index}.is_active`}
                  label={false}
                  fullWidth
                />
              </EditInputsWrap>
              <Hidden xsDown>
                <Grid item md={3} />
              </Hidden>
              <EditInputsWrap label={translate('ra.field.title')}>
                <TextInput source={`sectors.${index}.title`} label={false} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.button_text')}>
                <TextInput source={`sectors.${index}.text_link`} label={false} fullWidth />
              </EditInputsWrap>
              <EditInputsWrap label={translate('ra.field.link_button')}>
                <TextInput source={`sectors.${index}.link`} label={false} fullWidth />
              </EditInputsWrap>
              <FormDataConsumer>
                {({ formData }) => {
                  if (formData.sectors[index].type === BANNER_TYPE) {
                    return (
                      <Grid item xs={12}>
                        <Typography>
                          <b>{translate('ra.field.banners')}</b>
                        </Typography>

                        <Grid container style={{ paddingTop: 10 }}>
                          {formData.sectors[index]?.banners?.map((elem, i) => {
                            const options = banners.filter((banner) => {
                              if (elem.banner_id === banner.id) {
                                return true
                              }

                              return !formData.sectors[index]?.banners?.find(
                                (bannerAux) => bannerAux.banner_id === banner.id
                              )
                            })

                            return (
                              <>
                                <EditInputsWrap
                                  key={i}
                                  label={translate('ra.field.banner')}
                                  className='small'
                                  required
                                >
                                  {loadingBanners ? (
                                    <Skeleton variant='rectangular' width={'100%'} height={40} />
                                  ) : (
                                    <SelectInput
                                      source={`sectors.${index}.banners.${i}.banner_id`}
                                      optionText='short_name'
                                      optionValue='id'
                                      fullWidth
                                      choices={editMode && !('new_register' in elem) ? banners : options}
                                      label={false}
                                      disabled={editMode && !('new_register' in elem)}
                                      validate={[required()]}
                                      onChange={(e) =>
                                        handleChangeBannerBySector(
                                          formData.sectors[index],
                                          index,
                                          i,
                                          e.target.value
                                        )
                                      }
                                    />
                                  )}
                                </EditInputsWrap>
                                <EditInputsWrap
                                  label={translate('ra.field.is_active')}
                                  md={1}
                                  className='small'
                                >
                                  <BooleanInput
                                    defaultValue={true}
                                    source={`sectors.${index}.banners.${i}.is_active`}
                                    label={false}
                                    fullWidth
                                  />
                                </EditInputsWrap>
                                <Grid item xs={7} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <Button
                                    onClick={() => deleteBannerBySector(index, i, editMode)}
                                    startIcon={<Delete />}
                                    color='error'
                                    style={{ margin: 'auto 0px' }}
                                  >
                                    {translate('ra.action.delete')} {translate('ra.field.banner')}
                                  </Button>
                                </Grid>
                              </>
                            )
                          })}

                          <Grid item xs={12} style={{ paddingLeft: 15 }}>
                            <Button
                              startIcon={<Add />}
                              onClick={() => addBannerBySector(formData.sectors, index)}
                            >
                              {translate('ra.action.add_specific', {
                                name: 'Banner',
                              })}
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    )
                  } else if (formData.sectors[index].type === HTML_TYPE) {
                    return (
                      <Grid item xs={12}>
                        <Typography>
                          <b>{translate('ra.field.html')}</b>
                        </Typography>
                        <HTMLInputLanding source={`sectors.${index}.html_content`} validate={[required()]} />
                      </Grid>
                    )
                  }
                }}
              </FormDataConsumer>
            </Grid>
            <Grid container spacing={3} justifyContent='flex-end' className='addItemSeparator'>
              <Grid item xs={12} className='grid' style={{ marginTop: 10 }}>
                <Button
                  onClick={() => deleteSector(index, field, remove, editMode)}
                  startIcon={<Delete />}
                  color='error'
                >
                  {translate('ra.action.delete')} {translate('ra.field.sector')}
                </Button>
              </Grid>
            </Grid>
          </div>
        ))
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Button
          startIcon={<Add />}
          onClick={() => {
            append({
              is_active: true,
              new_register: true,
              banners: [],
              order: fields.length + 1,
              html_content: null,
              type: null,
            })
          }}
        >
          {translate('ra.action.add_specific', {
            name: translate('ra.field.sector'),
          })}
        </Button>

        <FormDataConsumer>{({ formData }) => <PreviewLanding landing={formData} />}</FormDataConsumer>
      </div>
    </>
  )
}
